import R from 'ramda';
import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';

type PendingViewEvent = {
  viewStartFn: () => void;
  viewEndFn: () => void;
};

/**
 * Store pending view events outside of React so that we can access them from window event listeners
 * that need to be registered early, indepedently of React components using telemetry.
 */
const pendingViewEvents: Map<string, PendingViewEvent> = new Map();

let isInitialized = false;
let notifyError: ((error: Error) => void) | undefined;

const handleError = (message: string) => {
  const fullMessage = `Telemetry: ${message}`;
  console.error(fullMessage);
  notifyError?.(new Error(fullMessage));
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    // Send view end events if the page is hidden
    pendingViewEvents.forEach(({ viewEndFn }) => viewEndFn());
  } else {
    // Restart view tracking if the page is shown again
    pendingViewEvents.forEach(({ viewStartFn }) => viewStartFn());
  }
};

/**
 * Registers needed listeners for page visibility changes, so that we can send tracking events
 * before the user closes/hides the tab or moves to another site.
 *
 * Also sets the error callback for internal telemetry errors.
 *
 * NOTE: This needs to be called before browser-telemetry is initialised, so that we receive the
 * events before it does, and can generate our events before browser-telemetry sends them.
 *
 * @param onError Called if we detect an internal error in the telemetry handling code. Errors are
 *                automatically logged as errors in the console, but you can use this callback to
 *                e.g. send the error to your error tracker.
 */
export const initTelemetryViewEvents = (onError: (error: Error) => void) => {
  // Listening to `visibilitychange` is the recommended way to detect when the user session is about
  // to end (temporarily or permanently):
  // https://developer.mozilla.org/en-US/docs/Web/API/Document/visibilitychange_event#usage_notes.
  //
  // However, when that event fires, on Firefox it's too late to send networks requests via `fetch`.
  // `navigator.sendBeacon()` would work but doesn't support custom headers which are currently
  // required by Gatekeeper. So browser-telemetry uses `fetch`, and this means we don't detect views
  // that were ended by the user navigating to another site (on Firefox).
  //
  // To work around this, we listen to `beforeunload` events too in e.g. browser-telemetry, but
  // those events are cancelable, and it's not straightforward to know if they were canceled.
  // So handling them would make our logic more complex.
  // `beforeunload` also has its own problems and is only recommended to use if you need to prompt
  // the user before leaving the site:
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event#usage_notes.
  window.addEventListener('visibilitychange', handleVisibilityChange);
  notifyError = onError;
  isInitialized = true;
};

const startPendingViewTracking = (trackingKey: string, pendingEvent: PendingViewEvent) => {
  if (!isInitialized) {
    handleError('initTelemetryViewEvents must be called before starting tracking');
    return;
  }
  if (pendingViewEvents.has(trackingKey)) {
    handleError(`tracking key ${trackingKey} already exists in pending view events`);
  }
  pendingViewEvents.set(trackingKey, pendingEvent);
  pendingEvent.viewStartFn();
};

const endPendingViewTracking = (trackingKey: string) => {
  const pendingEvent = pendingViewEvents.get(trackingKey);
  if (!pendingEvent) {
    handleError(`pending view event with tracking key ${trackingKey} not found`);
    return;
  }
  pendingEvent.viewEndFn();
  pendingViewEvents.delete(trackingKey);
};

/**
 * A helper for sending **view** telemetry events after the viewing ends.
 *
 * How it works:
 * 1. When receiving **non-undefined `key` and `context`** values, stores the current time as view
 *    start time and a copy of the context; the context is deeply cloned to keep the original value.
 * 2. When `key` changes, calls `sendEventCb`, passing it the duration from the view start time and
 *    stored `context`.
 * 3. If `key` and `context` are still non-undefined, starts over from step 1. for the current key.
 *
 * In addition to `key` change, `sendEventCb` is also called if one of these events happens:
 * 1. The component using the hook is unmounted.
 * 2. The user hides the page (`visibilitychange` event with state `hidden`); if the page becomes
 *    visible again, restarts tracking for the same view.
 *
 * @param key Internal key to identify the view event. Use `undefined` before you want to start
 *            tracking.
 * @param context Context values needed for the telemetry event. You can use `undefined` to delay
 *                the start of tracking until you have the full context, even if you pass a
 *                non-undefined `key`.
 * @param sendEventCb Callback that sends the telemetry event. Should call an Avo function to send
 *                    it.
 */
export const useTelemetryViewEvent = <T>(
  key: string | undefined,
  context: T | undefined,
  sendEventCb: (durationMs: number, context: T) => void,
) => {
  // Use references for state; they are available even when the component is unmounted or the user
  // session is ending
  const previousKeyRef = useRef<string>();
  const trackingKeyRef = useRef<string>();
  const startTimeRef = useRef<number>();
  const contextRef = useRef<T>();
  const isCleanedUp = useRef(false);

  const handleViewStart = () => {
    startTimeRef.current = Date.now();
  };

  const handleViewEnd = () => {
    if (startTimeRef.current === undefined) {
      handleError('start time not set');
      return;
    }
    const duration = Date.now() - startTimeRef.current;
    sendEventCb(duration, contextRef.current as T);
  };

  // Ensure view end is registered on unmount
  useEffect(() => {
    // Restart tracking if setup is called again after cleanup (happens in strict mode)
    if (isCleanedUp.current && trackingKeyRef.current) {
      startPendingViewTracking(trackingKeyRef.current, {
        viewStartFn: handleViewStart,
        viewEndFn: handleViewEnd,
      });
      isCleanedUp.current = false;
    }
    return () => {
      if (trackingKeyRef.current && isInitialized) {
        endPendingViewTracking(trackingKeyRef.current);
        isCleanedUp.current = true;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Do nothing as long as the key stays the same
  if (key === previousKeyRef.current) {
    return;
  }

  if (trackingKeyRef.current) {
    endPendingViewTracking(trackingKeyRef.current);

    previousKeyRef.current = undefined;
    trackingKeyRef.current = undefined;
  }

  if (key !== undefined && context !== undefined) {
    previousKeyRef.current = key;
    trackingKeyRef.current = uuidv4();
    contextRef.current = R.clone(context);

    startPendingViewTracking(trackingKeyRef.current, {
      viewStartFn: handleViewStart,
      viewEndFn: handleViewEnd,
    });
  }
};
